import type {Configuration} from '../configuration';
import type {AxiosInstance, AxiosPromise, RawAxiosRequestConfig} from 'axios';
import {BASE_PATH, BaseAPI, operationServerMap, RequestArgs} from '../base';
import globalAxios from 'axios';
import {
  assertParamExists,
  createRequestFunction,
  DUMMY_BASE_URL,
  IdRsp,
  serializeDataIfNeeded,
  setSearchParams,
  toPathString,
} from '../common';
import {
  IntercomDevice,
  IntercomDeviceCreateReq,
  IntercomDeviceRegisterReq,
  IntercomDevices,
  IntercomDeviceUpdateReq,
  IntercomManagerAssignRsp,
  IntercomManagerEditRsp,
  IntercomManagerBulkAssignReq,
  IntercomManagerBulkAssignRsp,
  IntercomManagerReplaceReq,
  IntercomManagerReplaceRsp,
  DeviceModulesRsp,
  DeviceModulesCreateReq,
  AvailableModulesRsp,
  DeviceLogoUploadRsp,
  DeviceLogoGetRsp,
  DeviceLogoDeleteRsp,
  RoomConfigRsp,
  RoomConfigUpdateReq,
} from '../types/intercomDevice';

/**
 * IntercomDevicePublicServiceApi - axios parameter creator
 * @export
 */
export const IntercomDevicePublicServiceApiAxiosParamCreator = function (
  configuration?: Configuration
) {
  return {
    /**
     * 设备注册
     * @summary 设备注册
     * @param {IntercomDeviceRegisterReq} intercomDeviceRegisterReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDevicePublicServiceRegister: async (
      intercomDeviceRegisterReq: IntercomDeviceRegisterReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'intercomDeviceRegisterReq' is not null or undefined
      assertParamExists(
        'intercomDevicePublicServiceRegister',
        'intercomDeviceRegisterReq',
        intercomDeviceRegisterReq
      );
      const localVarPath = `/public/intercom_device/initialize`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        intercomDeviceRegisterReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
  };
};

/**
 * IntercomDevicePublicServiceApi - functional programming interface
 * @export
 */
export const IntercomDevicePublicServiceApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = IntercomDevicePublicServiceApiAxiosParamCreator(configuration);
  return {
    /**
     * 设备注册
     * @summary 设备注册
     * @param {IntercomDeviceRegisterReq} intercomDeviceRegisterReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDevicePublicServiceRegister(
      intercomDeviceRegisterReq: IntercomDeviceRegisterReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDevicePublicServiceRegister(
        intercomDeviceRegisterReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDevicePublicServiceApi.intercomDevicePublicServiceRegister']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
  };
};

/**
 * IntercomDevicePublicServiceApi - factory interface
 * @export
 */
export const IntercomDevicePublicServiceApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = IntercomDevicePublicServiceApiFp(configuration);
  return {
    /**
     * 设备注册
     * @summary 设备注册
     * @param {IntercomDeviceRegisterReq} intercomDeviceRegisterReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDevicePublicServiceRegister(
      intercomDeviceRegisterReq: IntercomDeviceRegisterReq,
      options?: any
    ): AxiosPromise<void> {
      return localVarFp
        .intercomDevicePublicServiceRegister(intercomDeviceRegisterReq, options)
        .then(request => request(axios, basePath));
    },
  };
};

/**
 * IntercomDevicePublicServiceApi - object-oriented interface
 * @export
 * @class IntercomDevicePublicServiceApi
 * @extends {BaseAPI}
 */
export class IntercomDevicePublicServiceApi extends BaseAPI {
  /**
   * 设备注册
   * @summary 设备注册
   * @param {IntercomDeviceRegisterReq} intercomDeviceRegisterReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDevicePublicServiceApi
   */
  public intercomDevicePublicServiceRegister(
    intercomDeviceRegisterReq: IntercomDeviceRegisterReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDevicePublicServiceApiFp(this.configuration)
      .intercomDevicePublicServiceRegister(intercomDeviceRegisterReq, options)
      .then(request => request(this.axios, this.basePath));
  }
}

/**
 * IntercomDeviceServiceApi - axios parameter creator
 * @export
 */
export const IntercomDeviceServiceApiAxiosParamCreator = function (configuration?: Configuration) {
  return {
    /**
     * 对讲机设备创建
     * @summary 对讲机设备创建
     * @param {IntercomDeviceCreateReq} intercomDeviceCreateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceCreate: async (
      intercomDeviceCreateReq: IntercomDeviceCreateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'intercomDeviceCreateReq' is not null or undefined
      assertParamExists(
        'intercomDeviceServiceCreate',
        'intercomDeviceCreateReq',
        intercomDeviceCreateReq
      );
      const localVarPath = `/intercom_device/intercom_device`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        intercomDeviceCreateReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 对讲机设备删除
     * @summary 对讲机设备删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceDelete: async (
      id: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('intercomDeviceServiceDelete', 'id', id);
      const localVarPath = `/intercom_device/intercom_device/{id}`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'DELETE', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 对讲机设备详情
     * @summary 对讲机设备详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceDetail: async (
      id: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('intercomDeviceServiceDetail', 'id', id);
      const localVarPath = `/intercom_device/intercom_device/{id}`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 对讲机设备查询
     * @summary 对讲机设备查询
     * @param {string} [orgId]
     * @param {Array<string>} [ids]
     * @param {string} [name] search name
     * @param {Array<string>} [apartmentIds]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceFind: async (
      orgId?: string,
      ids?: Array<string>,
      name?: string,
      apartmentIds?: Array<string>,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      const localVarPath = `/intercom_device/intercom_device`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      if (orgId !== undefined) {
        localVarQueryParameter['orgId'] = orgId;
      }

      if (ids) {
        localVarQueryParameter['ids'] = ids;
      }

      if (name !== undefined) {
        localVarQueryParameter['name'] = name;
      }

      if (apartmentIds) {
        localVarQueryParameter['apartmentIds'] = apartmentIds;
      }

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 对讲机设备修改
     * @summary 对讲机设备修改
     * @param {string} intercomDeviceId
     * @param {IntercomDeviceUpdateReq} intercomDeviceUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceUpdate: async (
      intercomDeviceId: string,
      intercomDeviceUpdateReq: IntercomDeviceUpdateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'intercomDeviceId' is not null or undefined
      assertParamExists('intercomDeviceServiceUpdate', 'intercomDeviceId', intercomDeviceId);
      // verify required parameter 'intercomDeviceUpdateReq' is not null or undefined
      assertParamExists(
        'intercomDeviceServiceUpdate',
        'intercomDeviceUpdateReq',
        intercomDeviceUpdateReq
      );
      const localVarPath = `/intercom_device/intercom_device/{intercomDeviceId}`.replace(
        `{${'intercomDeviceId'}}`,
        encodeURIComponent(String(intercomDeviceId))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        intercomDeviceUpdateReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 分配 Intercom Manager
     * @summary 分配 Intercom Manager
     * @param {string} deviceId
     * @param {string} userId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    assignIntercomManager: async (
      deviceId: string,
      userId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('assignIntercomManager', 'deviceId', deviceId);
      assertParamExists('assignIntercomManager', 'userId', userId);
      const localVarPath = `/intercom_device/intercom-device/${deviceId}/manager/${userId}`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 取消分配 Intercom Manager
     * @summary 取消分配 Intercom Manager
     * @param {string} deviceId
     * @param {string} userId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    unassignIntercomManager: async (
      deviceId: string,
      userId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('unassignIntercomManager', 'deviceId', deviceId);
      assertParamExists('unassignIntercomManager', 'userId', userId);
      const localVarPath = `/intercom_device/intercom-device/${deviceId}/manager/${userId}`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'DELETE', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;
      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 修改 Intercom Manager
     * @summary 修改 Intercom Manager
     * @param {string} deviceId
     * @param {string} userId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    editIntercomManager: async (
      deviceId: string,
      userId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('editIntercomManager', 'deviceId', deviceId);
      assertParamExists('editIntercomManager', 'userId', userId);
      const localVarPath = `/intercom_device/intercom-device/${deviceId}/manager/${userId}`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;
      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 获取设备模块列表
     * @summary 获取设备模块列表
     * @param {string} deviceId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getDeviceModules: async (
      deviceId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('getDeviceModules', 'deviceId', deviceId);
      const localVarPath = `/intercom_device/admin/${deviceId}/modules`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;
      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 创建设备模块
     * @summary 创建设备模块
     * @param {string} deviceId
     * @param {DeviceModulesCreateReq} deviceModulesCreateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    createDeviceModules: async (
      deviceId: string,
      deviceModulesCreateReq: DeviceModulesCreateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('createDeviceModules', 'deviceId', deviceId);
      assertParamExists('createDeviceModules', 'deviceModulesCreateReq', deviceModulesCreateReq);
      const localVarPath = `/intercom_device/admin/${deviceId}/modules`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        deviceModulesCreateReq,
        localVarRequestOptions,
        configuration
      );
      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 获取可用模块列表
     * @summary 获取可用模块列表
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getAvailableModules: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
      const localVarPath = `/intercom_device/available-modules`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;
      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 批量分配 Intercom Manager
     * @summary 批量分配 Intercom Manager
     * @param {string} deviceId
     * @param {IntercomManagerBulkAssignReq} intercomManagerBulkAssignReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    bulkAssignIntercomManagers: async (
      deviceId: string,
      intercomManagerBulkAssignReq: IntercomManagerBulkAssignReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('bulkAssignIntercomManagers', 'deviceId', deviceId);
      assertParamExists(
        'bulkAssignIntercomManagers',
        'intercomManagerBulkAssignReq',
        intercomManagerBulkAssignReq
      );
      const localVarPath = `/intercom_device/intercom-device/${deviceId}/managers/bulk`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        intercomManagerBulkAssignReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * 替换所有 Intercom Manager
     * @summary 替换所有 Intercom Manager
     * @param {string} deviceId
     * @param {IntercomManagerReplaceReq} intercomManagerReplaceReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    replaceIntercomManagers: async (
      deviceId: string,
      intercomManagerReplaceReq: IntercomManagerReplaceReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('replaceIntercomManagers', 'deviceId', deviceId);
      assertParamExists(
        'replaceIntercomManagers',
        'intercomManagerReplaceReq',
        intercomManagerReplaceReq
      );
      const localVarPath = `/intercom_device/intercom-device/${deviceId}/managers`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        intercomManagerReplaceReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * 上传设备 Logo
     * @summary 上传设备 Logo
     * @param {string} deviceId
     * @param {File} logo
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    uploadDeviceLogo: async (
      deviceId: string,
      logo: File,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('uploadDeviceLogo', 'deviceId', deviceId);
      assertParamExists('uploadDeviceLogo', 'logo', logo);
      const localVarPath = `/intercom_device/intercom_device/${deviceId}/logo`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;
      const localVarFormParams = new FormData();

      if (logo !== undefined) {
        localVarFormParams.append('logo', logo);
      }

      localVarHeaderParameter['Content-Type'] = 'multipart/form-data';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = localVarFormParams;

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * 获取设备 Logo
     * @summary 获取设备 Logo
     * @param {string} deviceId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getDeviceLogo: async (
      deviceId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('getDeviceLogo', 'deviceId', deviceId);
      const localVarPath = `/intercom_device/intercom_device/${deviceId}/logo`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },

    /**
     * 删除设备 Logo
     * @summary 删除设备 Logo
     * @param {string} deviceId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    deleteDeviceLogo: async (
      deviceId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('deleteDeviceLogo', 'deviceId', deviceId);
      const localVarPath = `/intercom_device/intercom_device/${deviceId}/logo`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'DELETE', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 获取房间配置
     * @summary 获取房间配置
     * @param {string} deviceId
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getRoomConfig: async (
      deviceId: string,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('getRoomConfig', 'deviceId', deviceId);
      const localVarPath = `/intercom_device/intercom_device/${deviceId}/room-config`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
    /**
     * 更新房间配置
     * @summary 更新房间配置
     * @param {string} deviceId
     * @param {RoomConfigUpdateReq} roomConfigUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    updateRoomConfig: async (
      deviceId: string,
      roomConfigUpdateReq: RoomConfigUpdateReq,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      assertParamExists('updateRoomConfig', 'deviceId', deviceId);
      assertParamExists('updateRoomConfig', 'roomConfigUpdateReq', roomConfigUpdateReq);
      const localVarPath = `/intercom_device/intercom_device/${deviceId}/room-config`;
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }
      const localVarRequestOptions = {method: 'PUT', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers,
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        roomConfigUpdateReq,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions,
      };
    },
  };
};

/**
 * IntercomDeviceServiceApi - functional programming interface
 * @export
 */
export const IntercomDeviceServiceApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = IntercomDeviceServiceApiAxiosParamCreator(configuration);
  return {
    /**
     * 对讲机设备创建
     * @summary 对讲机设备创建
     * @param {IntercomDeviceCreateReq} intercomDeviceCreateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDeviceServiceCreate(
      intercomDeviceCreateReq: IntercomDeviceCreateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IdRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDeviceServiceCreate(
        intercomDeviceCreateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.intercomDeviceServiceCreate']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 对讲机设备删除
     * @summary 对讲机设备删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDeviceServiceDelete(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDeviceServiceDelete(
        id,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.intercomDeviceServiceDelete']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 对讲机设备详情
     * @summary 对讲机设备详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDeviceServiceDetail(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomDevice>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDeviceServiceDetail(
        id,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.intercomDeviceServiceDetail']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 对讲机设备查询
     * @summary 对讲机设备查询
     * @param {string} [orgId]
     * @param {Array<string>} [ids]
     * @param {string} [name] search name
     * @param {Array<string>} [apartmentIds]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDeviceServiceFind(
      orgId?: string,
      ids?: Array<string>,
      name?: string,
      apartmentIds?: Array<string>,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomDevices>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDeviceServiceFind(
        orgId,
        ids,
        name,
        apartmentIds,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.intercomDeviceServiceFind']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * 对讲机设备修改
     * @summary 对讲机设备修改
     * @param {string} intercomDeviceId
     * @param {IntercomDeviceUpdateReq} intercomDeviceUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async intercomDeviceServiceUpdate(
      intercomDeviceId: string,
      intercomDeviceUpdateReq: IntercomDeviceUpdateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.intercomDeviceServiceUpdate(
        intercomDeviceId,
        intercomDeviceUpdateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.intercomDeviceServiceUpdate']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async assignIntercomManager(
      deviceId: string,
      userId: string,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomManagerAssignRsp>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.assignIntercomManager(
        deviceId,
        userId,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.assignIntercomManager']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async unassignIntercomManager(
      deviceId: string,
      userId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.unassignIntercomManager(
        deviceId,
        userId,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.unassignIntercomManager']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async editIntercomManager(
      deviceId: string,
      userId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomManagerEditRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.editIntercomManager(
        deviceId,
        userId,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.editIntercomManager']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async getDeviceModules(
      deviceId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeviceModulesRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.getDeviceModules(deviceId, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.getDeviceModules']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async createDeviceModules(
      deviceId: string,
      deviceModulesCreateReq: DeviceModulesCreateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeviceModulesRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.createDeviceModules(
        deviceId,
        deviceModulesCreateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.createDeviceModules']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async getAvailableModules(
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<AvailableModulesRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.getAvailableModules(options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.getAvailableModules']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async bulkAssignIntercomManagers(
      deviceId: string,
      intercomManagerBulkAssignReq: IntercomManagerBulkAssignReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomManagerBulkAssignRsp>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.bulkAssignIntercomManagers(
        deviceId,
        intercomManagerBulkAssignReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.bulkAssignIntercomManagers']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    async replaceIntercomManagers(
      deviceId: string,
      intercomManagerReplaceReq: IntercomManagerReplaceReq,
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<IntercomManagerReplaceRsp>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.replaceIntercomManagers(
        deviceId,
        intercomManagerReplaceReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.replaceIntercomManagers']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    async uploadDeviceLogo(
      deviceId: string,
      logo: File,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeviceLogoUploadRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.uploadDeviceLogo(
        deviceId,
        logo,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.uploadDeviceLogo']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    async getDeviceLogo(
      deviceId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeviceLogoGetRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.getDeviceLogo(deviceId, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.getDeviceLogo']?.[localVarOperationServerIndex]
          ?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },

    async deleteDeviceLogo(
      deviceId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DeviceLogoDeleteRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.deleteDeviceLogo(deviceId, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.deleteDeviceLogo']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async getRoomConfig(
      deviceId: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RoomConfigRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.getRoomConfig(deviceId, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.getRoomConfig']?.[localVarOperationServerIndex]
          ?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    async updateRoomConfig(
      deviceId: string,
      roomConfigUpdateReq: RoomConfigUpdateReq,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<RoomConfigRsp>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.updateRoomConfig(
        deviceId,
        roomConfigUpdateReq,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['IntercomDeviceServiceApi.updateRoomConfig']?.[
          localVarOperationServerIndex
        ]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
  };
};

/**
 * IntercomDeviceServiceApi - factory interface
 * @export
 */
export const IntercomDeviceServiceApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = IntercomDeviceServiceApiFp(configuration);
  return {
    /**
     * 对讲机设备创建
     * @summary 对讲机设备创建
     * @param {IntercomDeviceCreateReq} intercomDeviceCreateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceCreate(
      intercomDeviceCreateReq: IntercomDeviceCreateReq,
      options?: any
    ): AxiosPromise<IdRsp> {
      return localVarFp
        .intercomDeviceServiceCreate(intercomDeviceCreateReq, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 对讲机设备删除
     * @summary 对讲机设备删除
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceDelete(id: string, options?: any): AxiosPromise<void> {
      return localVarFp
        .intercomDeviceServiceDelete(id, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 对讲机设备详情
     * @summary 对讲机设备详情
     * @param {string} id
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceDetail(id: string, options?: any): AxiosPromise<IntercomDevice> {
      return localVarFp
        .intercomDeviceServiceDetail(id, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 对讲机设备查询
     * @summary 对讲机设备查询
     * @param {string} [orgId]
     * @param {Array<string>} [ids]
     * @param {string} [name] search name
     * @param {Array<string>} [apartmentIds]
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceFind(
      orgId?: string,
      ids?: Array<string>,
      name?: string,
      apartmentIds?: Array<string>,
      options?: any
    ): AxiosPromise<IntercomDevices> {
      return localVarFp
        .intercomDeviceServiceFind(orgId, ids, name, apartmentIds, options)
        .then(request => request(axios, basePath));
    },
    /**
     * 对讲机设备修改
     * @summary 对讲机设备修改
     * @param {string} intercomDeviceId
     * @param {IntercomDeviceUpdateReq} intercomDeviceUpdateReq
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    intercomDeviceServiceUpdate(
      intercomDeviceId: string,
      intercomDeviceUpdateReq: IntercomDeviceUpdateReq,
      options?: any
    ): AxiosPromise<void> {
      return localVarFp
        .intercomDeviceServiceUpdate(intercomDeviceId, intercomDeviceUpdateReq, options)
        .then(request => request(axios, basePath));
    },
    assignIntercomManager(
      deviceId: string,
      userId: string,
      options?: any
    ): AxiosPromise<IntercomManagerAssignRsp> {
      return localVarFp
        .assignIntercomManager(deviceId, userId, options)
        .then(request => request(axios, basePath));
    },
    unassignIntercomManager(deviceId: string, userId: string, options?: any): AxiosPromise<void> {
      return localVarFp
        .unassignIntercomManager(deviceId, userId, options)
        .then(request => request(axios, basePath));
    },
    editIntercomManager(
      deviceId: string,
      userId: string,
      options?: any
    ): AxiosPromise<IntercomManagerEditRsp> {
      return localVarFp
        .editIntercomManager(deviceId, userId, options)
        .then(request => request(axios, basePath));
    },
    getDeviceModules(deviceId: string, options?: any): AxiosPromise<DeviceModulesRsp> {
      return localVarFp
        .getDeviceModules(deviceId, options)
        .then(request => request(axios, basePath));
    },
    createDeviceModules(
      deviceId: string,
      deviceModulesCreateReq: DeviceModulesCreateReq,
      options?: any
    ): AxiosPromise<DeviceModulesRsp> {
      return localVarFp
        .createDeviceModules(deviceId, deviceModulesCreateReq, options)
        .then(request => request(axios, basePath));
    },
    getAvailableModules(options?: any): AxiosPromise<AvailableModulesRsp> {
      return localVarFp.getAvailableModules(options).then(request => request(axios, basePath));
    },
    bulkAssignIntercomManagers(
      deviceId: string,
      intercomManagerBulkAssignReq: IntercomManagerBulkAssignReq,
      options?: any
    ): AxiosPromise<IntercomManagerBulkAssignRsp> {
      return localVarFp
        .bulkAssignIntercomManagers(deviceId, intercomManagerBulkAssignReq, options)
        .then(request => request(axios, basePath));
    },
    replaceIntercomManagers(
      deviceId: string,
      intercomManagerReplaceReq: IntercomManagerReplaceReq,
      options?: any
    ): AxiosPromise<IntercomManagerReplaceRsp> {
      return localVarFp
        .replaceIntercomManagers(deviceId, intercomManagerReplaceReq, options)
        .then(request => request(axios, basePath));
    },
    uploadDeviceLogo(
      deviceId: string,
      logo: File,
      options?: any
    ): AxiosPromise<DeviceLogoUploadRsp> {
      return localVarFp
        .uploadDeviceLogo(deviceId, logo, options)
        .then(request => request(axios, basePath));
    },
    getDeviceLogo(deviceId: string, options?: any): AxiosPromise<DeviceLogoGetRsp> {
      return localVarFp.getDeviceLogo(deviceId, options).then(request => request(axios, basePath));
    },
    deleteDeviceLogo(deviceId: string, options?: any): AxiosPromise<DeviceLogoDeleteRsp> {
      return localVarFp
        .deleteDeviceLogo(deviceId, options)
        .then(request => request(axios, basePath));
    },
    getRoomConfig(deviceId: string, options?: any): AxiosPromise<RoomConfigRsp> {
      return localVarFp.getRoomConfig(deviceId, options).then(request => request(axios, basePath));
    },
    updateRoomConfig(
      deviceId: string,
      roomConfigUpdateReq: RoomConfigUpdateReq,
      options?: any
    ): AxiosPromise<RoomConfigRsp> {
      return localVarFp
        .updateRoomConfig(deviceId, roomConfigUpdateReq, options)
        .then(request => request(axios, basePath));
    },
  };
};

/**
 * IntercomDeviceServiceApi - object-oriented interface
 * @export
 * @class IntercomDeviceServiceApi
 * @extends {BaseAPI}
 */
export class IntercomDeviceServiceApi extends BaseAPI {
  /**
   * 对讲机设备创建
   * @summary 对讲机设备创建
   * @param {IntercomDeviceCreateReq} intercomDeviceCreateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public intercomDeviceServiceCreate(
    intercomDeviceCreateReq: IntercomDeviceCreateReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .intercomDeviceServiceCreate(intercomDeviceCreateReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 对讲机设备删除
   * @summary 对讲机设备删除
   * @param {string} id
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public intercomDeviceServiceDelete(id: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .intercomDeviceServiceDelete(id, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 对讲机设备详情
   * @summary 对讲机设备详情
   * @param {string} id
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public intercomDeviceServiceDetail(id: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .intercomDeviceServiceDetail(id, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 对讲机设备查询
   * @summary 对讲机设备查询
   * @param {string} [orgId]
   * @param {Array<string>} [ids]
   * @param {string} [name] search name
   * @param {Array<string>} [apartmentIds]
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public intercomDeviceServiceFind(
    orgId?: string,
    ids?: Array<string>,
    name?: string,
    apartmentIds?: Array<string>,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .intercomDeviceServiceFind(orgId, ids, name, apartmentIds, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 对讲机设备修改
   * @summary 对讲机设备修改
   * @param {string} intercomDeviceId
   * @param {IntercomDeviceUpdateReq} intercomDeviceUpdateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public intercomDeviceServiceUpdate(
    intercomDeviceId: string,
    intercomDeviceUpdateReq: IntercomDeviceUpdateReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .intercomDeviceServiceUpdate(intercomDeviceId, intercomDeviceUpdateReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 分配 Intercom Manager
   * @param {string} deviceId
   * @param {string} userId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public assignIntercomManager(deviceId: string, userId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .assignIntercomManager(deviceId, userId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 取消分配 Intercom Manager
   * @param {string} deviceId
   * @param {string} userId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public unassignIntercomManager(
    deviceId: string,
    userId: string,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .unassignIntercomManager(deviceId, userId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 修改 Intercom Manager
   * @param {string} deviceId
   * @param {string} userId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public editIntercomManager(deviceId: string, userId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .editIntercomManager(deviceId, userId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 获取设备模块列表
   * @param {string} deviceId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public getDeviceModules(deviceId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .getDeviceModules(deviceId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 创建设备模块
   * @param {string} deviceId
   * @param {DeviceModulesCreateReq} deviceModulesCreateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public createDeviceModules(
    deviceId: string,
    deviceModulesCreateReq: DeviceModulesCreateReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .createDeviceModules(deviceId, deviceModulesCreateReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 获取可用模块列表
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public getAvailableModules(options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .getAvailableModules(options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 批量分配 Intercom Manager
   * @param {string} deviceId
   * @param {IntercomManagerBulkAssignReq} intercomManagerBulkAssignReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public bulkAssignIntercomManagers(
    deviceId: string,
    intercomManagerBulkAssignReq: IntercomManagerBulkAssignReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .bulkAssignIntercomManagers(deviceId, intercomManagerBulkAssignReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 替换所有 Intercom Manager
   * @param {string} deviceId
   * @param {IntercomManagerReplaceReq} intercomManagerReplaceReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public replaceIntercomManagers(
    deviceId: string,
    intercomManagerReplaceReq: IntercomManagerReplaceReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .replaceIntercomManagers(deviceId, intercomManagerReplaceReq, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 上传设备 Logo
   * @param {string} deviceId
   * @param {File} logo
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public uploadDeviceLogo(deviceId: string, logo: File, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .uploadDeviceLogo(deviceId, logo, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 获取设备 Logo
   * @param {string} deviceId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public getDeviceLogo(deviceId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .getDeviceLogo(deviceId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 删除设备 Logo
   * @param {string} deviceId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public deleteDeviceLogo(deviceId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .deleteDeviceLogo(deviceId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 获取房间配置
   * @param {string} deviceId
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public getRoomConfig(deviceId: string, options?: RawAxiosRequestConfig) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .getRoomConfig(deviceId, options)
      .then(request => request(this.axios, this.basePath));
  }

  /**
   * 更新房间配置
   * @param {string} deviceId
   * @param {RoomConfigUpdateReq} roomConfigUpdateReq
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof IntercomDeviceServiceApi
   */
  public updateRoomConfig(
    deviceId: string,
    roomConfigUpdateReq: RoomConfigUpdateReq,
    options?: RawAxiosRequestConfig
  ) {
    return IntercomDeviceServiceApiFp(this.configuration)
      .updateRoomConfig(deviceId, roomConfigUpdateReq, options)
      .then(request => request(this.axios, this.basePath));
  }
}
